import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, Sparkles } from 'lucide-react';
import { getWithExpiry, setWithExpiry } from '../utils/storage';
import { LOGO_URL } from '../data/constants';

const VISIT_KEY = 'bodyup_app_visit_count';
const DISMISS_KEY = 'bodyup_install_banner_dismissed';
const VISIT_THRESHOLD = 3;

/** Bannière discrète invitant à installer l'app PWA, affichée une seule fois
 * après quelques connexions (pas dès la 1ère visite, pour ne pas être intrusif). */
export const InstallAppBanner = ({ canInstall, onInstallClick, en = false }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!canInstall) return;
    if (getWithExpiry(DISMISS_KEY) === '1') return;

    const count = parseInt(getWithExpiry(VISIT_KEY) || '0', 10) + 1;
    setWithExpiry(VISIT_KEY, String(count));

    if (count >= VISIT_THRESHOLD) {
      const timer = setTimeout(() => setShow(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [canInstall]);

  const dismiss = (permanent) => {
    if (permanent) setWithExpiry(DISMISS_KEY, '1');
    setShow(false);
  };

  const handleInstall = async () => {
    await onInstallClick?.();
    dismiss(true);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
          className="fixed left-4 right-4 sm:left-6 sm:right-auto sm:max-w-sm bottom-44 lg:bottom-6 z-[55]"
          data-testid="install-app-banner"
        >
          <div className="relative bg-gradient-to-br from-zinc-900 to-black border border-primary/25 rounded-2xl p-4 shadow-2xl shadow-primary/20 overflow-hidden">
            <div className="absolute -top-8 -right-8 w-24 h-24 bg-primary/30 rounded-full blur-2xl" />
            <button
              onClick={() => dismiss(false)}
              className="absolute top-3 right-3 z-10 text-zinc-500 hover:text-white transition-colors"
              data-testid="close-install-banner-btn"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3 relative pr-5">
              <img src={LOGO_URL} alt="Body-Up" className="w-10 h-10 rounded-xl object-cover shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-white flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-yellow-400" /> {L('Installez Body-Up !', 'Install Body-Up!')}
                </p>
                <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                  {L("Ajoutez l'app à votre écran d'accueil pour un accès plus rapide et des notifications.", 'Add the app to your home screen for faster access and notifications.')}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2 mt-3 relative">
              <button onClick={() => dismiss(true)} className="text-xs text-zinc-500 hover:text-zinc-300 underline underline-offset-2" data-testid="dismiss-install-banner-permanently-btn">
                {L('Ne plus me proposer', "Don't ask again")}
              </button>
              <button
                onClick={handleInstall}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-primary to-pink-500 text-white text-xs font-bold hover:scale-105 transition-transform shrink-0"
                data-testid="install-app-banner-cta"
              >
                <Download className="w-3.5 h-3.5" /> {L('Installer', 'Install')}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InstallAppBanner;
